import { useCallback, useRef } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { InvitedAppShortcutAllowedAppKey } from '../../../common/types';

export const ANALYTICS_CHANNEL = FabricChannel.growth;

export const useInvitedAppAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isViewedInvitedAppShortcutsFired = useRef<boolean>(false);
	const isFeatureExposedFired = useRef<boolean>(false);

	/**
	 * Fire an analytics event when the user views the invited app shortcuts.
	 */
	const fireViewedInvitedAppShortcuts = useCallback(
		(apps: InvitedAppShortcutAllowedAppKey[]) => {
			if (isViewedInvitedAppShortcutsFired.current) {
				return;
			}

			createAnalyticsEvent({
				eventType: 'screen',
				name: 'invitedAppShortcuts',
				attributes: {
					apps,
				},
			}).fire(ANALYTICS_CHANNEL);

			isViewedInvitedAppShortcutsFired.current = true;
		},
		[createAnalyticsEvent],
	);

	const fireViewedInvitedAppShortcutPopup = useCallback(
		({ app, collaborators }: { app: InvitedAppShortcutAllowedAppKey; collaborators: number }) => {
			createAnalyticsEvent({
				eventType: 'screen',
				name: 'invitedAppShortcutPopup',
				attributes: {
					app,
					collaborators,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireLinkClickedAcceptInvite = useCallback(
		({ app }: { app: InvitedAppShortcutAllowedAppKey }) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'acceptInvite',
				attributes: {
					app,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireViewedMoreCollaboratorsTooltip = useCallback(
		({ app, collaborators }: { app: InvitedAppShortcutAllowedAppKey; collaborators: number }) => {
			createAnalyticsEvent({
				eventType: 'screen',
				name: 'moreCollaboratorsTooltip',
				attributes: {
					app,
					collaborators,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireInvitedAppShortcutsEligibilityChecked = useCallback(
		({ ineligibilityReason }: { ineligibilityReason: string }) => {
			createAnalyticsEvent({
				eventType: 'track',
				action: 'checked',
				actionSubject: 'eligibility',
				attributes: {
					flagKey: 'twcg-460-personalised-invite-notifications-m2',
					ineligibilityReason,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	/**
	 * Fire an analytics event when the feature is exposed.
	 * This event is fired only once per session.
	 * @param isEnabled - the experiment's isEnabled value
	 * @returns void
	 */
	const fireFeatureExposed = useCallback(
		({ isEnabled }: { isEnabled: boolean }) => {
			if (isFeatureExposedFired.current) {
				return;
			}

			createAnalyticsEvent({
				eventType: 'track',
				action: 'exposed',
				actionSubject: 'feature',
				attributes: {
					featureKey: 'twcg-460-personalised-invite-notifications-m2',
					cohort: isEnabled ? 'variant' : 'control',
				},
			}).fire(ANALYTICS_CHANNEL);

			isFeatureExposedFired.current = true;
		},
		[createAnalyticsEvent],
	);

	return {
		fireViewedInvitedAppShortcuts,
		fireViewedInvitedAppShortcutPopup,
		fireLinkClickedAcceptInvite,
		fireViewedMoreCollaboratorsTooltip,
		fireInvitedAppShortcutsEligibilityChecked,
		fireFeatureExposed,
	};
};
